import React, {
  useMemo,
  useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  AddNewButton,
  Card,
  Search,
} from '@makeably/creativex-design-system';
import {
  emptyState,
  getBoolDimension,
  getDateTimeDimension,
} from 'components/internal/shared';
import ItemsTable from 'components/molecules/ItemsTable';
import { getItemSortBy } from 'utilities/item';
import { searchItems } from 'utilities/itemSearch';
import {
  internalUserActAsPath,
  newInternalScoreSubscriptionPath,
  scorePath,
} from 'utilities/routes';
import {
  getPage,
  getParams,
} from 'utilities/url';
import styles from './Subscriptions.module.css';

const subscriptionProps = PropTypes.shape({
  companyId: PropTypes.number.isRequired,
  companyName: PropTypes.string.isRequired,
  createdAt: PropTypes.string.isRequired,
  defaultScore: PropTypes.bool.isRequired,
  scoreId: PropTypes.number.isRequired,
  showInDashboard: PropTypes.bool.isRequired,
  showInReporting: PropTypes.bool.isRequired,
  showInScorecard: PropTypes.bool.isRequired,
  userId: PropTypes.number.isRequired,
});

const propTypes = {
  scoreId: PropTypes.number.isRequired,
  scoreName: PropTypes.string.isRequired,
  subscriptions: PropTypes.arrayOf(subscriptionProps).isRequired,
};

const headers = [
  {
    key: 'id',
    label: 'Score ID',
  },
  {
    key: 'companyName',
    label: 'Company',
  },
  {
    key: 'createdAt',
    label: 'Subscribed',
  },
  {
    key: 'defaultScore',
    label: 'Default\nScore',
  },
  {
    key: 'showInDashboard',
    label: 'Show in\nDashboard',
  },
  {
    key: 'showInReporting',
    label: 'Show in\nReporting',
  },
  {
    key: 'showInScorecard',
    label: 'Show in Pre-Flight\nScorecard PDF',
  },
  {
    key: 'actAs',
    label: '',
    sortable: false,
  },
];

const searchKeys = [
  'companyName',
  'id',
];

function getItems(subscriptions) {
  return subscriptions.map(({
    companyId,
    companyName,
    createdAt,
    defaultScore,
    scoreId,
    showInDashboard,
    showInReporting,
    showInScorecard,
    userId,
  }) => ({
    actAs: {
      element: (
        <a
          className={`t-button ${styles.link}`}
          href={internalUserActAsPath(userId, { redirect_url: scorePath(scoreId) })}
          rel="noreferrer"
          target="_blank"
        >
          View Score
        </a>
      ),
      value: companyId,
    },
    companyId: { value: companyId },
    companyName: { value: companyName },
    createdAt: getDateTimeDimension(createdAt),
    defaultScore: getBoolDimension(defaultScore, true, true),
    id: { value: scoreId },
    showInDashboard: getBoolDimension(showInDashboard, true, true),
    showInReporting: getBoolDimension(showInReporting, true, true),
    showInScorecard: getBoolDimension(showInScorecard, true, true),
  }));
}

function Subscriptions({
  scoreId,
  scoreName,
  subscriptions,
}) {
  const params = getParams(window);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState({
    asc: true,
    key: 'companyName',
  });
  const [page, setPage] = useState(getPage(params));

  const items = useMemo(() => (
    getItems(subscriptions)
  ), [subscriptions]);

  const searchedItems = useMemo(() => {
    setPage(1);
    return searchItems(items, search, searchKeys);
  }, [items, search]);

  const sortedItems = useMemo(() => {
    const byKeyDir = getItemSortBy(sort.key, sort.asc);
    return searchedItems.slice().sort(byKeyDir);
  }, [searchedItems, sort]);

  return (
    <Card className="u-flexColumn u-gap-24">
      <h5>{ `Subscriptions for '${scoreName}' (${scoreId})` }</h5>
      <div className="u-flexRow u-justifyBetween u-gap-16">
        <Search
          placeholder="Search by Company, ID"
          value={search}
          onChange={setSearch}
        />
        <AddNewButton
          label="Add Subscriptions"
          url={newInternalScoreSubscriptionPath(scoreId)}
        />
      </div>
      <ItemsTable
        className="u-scrollShadowRight"
        emptyTableContent={emptyState}
        headers={headers}
        items={sortedItems}
        page={page}
        sort={sort}
        onPageChange={setPage}
        onSortChange={setSort}
      />
    </Card>
  );
}

Subscriptions.propTypes = propTypes;

export default Subscriptions;
